/**
 * Inactivity auto-logout (security audit finding #3).
 *
 * Logs the user out after IDLE_TIMEOUT_MS without any user interaction.
 * The backend enforces the same window server-side (20-minute access token
 * with sliding renewal) — this hook is the client half so an unattended
 * screen returns to the landing page instead of sitting on stale data.
 */
import { useEffect, useRef } from 'react';
import { authService } from '../../services/authService';
import { apiPost } from '../services/apiClient';

const IDLE_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes
// Resetting a timer on every mousemove is wasteful — refresh it at most
// once per RESET_GRANULARITY_MS. Worst-case logout is 20m + 30s.
const RESET_GRANULARITY_MS = 30 * 1000;

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
];

export function useIdleLogout(): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastResetRef = useRef<number>(0);

  useEffect(() => {
    const logoutForInactivity = async () => {
      try {
        // Best effort — clears the httpOnly cookies server-side
        await apiPost('/auth/logout', {});
      } catch {
        // Even if the API call fails, clear the client session below
      }
      authService.logout();
      // Full reload clears all in-memory state (Redux, caches)
      window.location.href = '/';
    };

    const startTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(logoutForInactivity, IDLE_TIMEOUT_MS);
    };

    const onActivity = () => {
      const now = Date.now();
      if (now - lastResetRef.current < RESET_GRANULARITY_MS) return;
      lastResetRef.current = now;
      startTimer();
    };

    startTimer();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
    };
  }, []);
}

export default useIdleLogout;
